import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, MousePointer, Phone, Star, DollarSign, Copy, Check, Mail, ExternalLink, MoreHorizontal, Share } from "lucide-react";
import { SiGoogle } from "react-icons/si";
import { isInAppBrowser, isAndroid, isIOS, androidChromeIntentUrl } from "@/lib/in-app-browser";
import logoBlack from "@assets/TORK_Logo_Black_1779647609157.png";
import logoWhite from "@assets/TORK_Logo_White_1779647609157.png";

function InAppBrowserCard({ tosChecked }: { tosChecked: boolean }) {
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [autoOpening, setAutoOpening] = useState(false);
  const android = isAndroid();
  const ios = isIOS();
  const currentUrl = typeof window !== "undefined" ? window.location.href : "";

  // Android: the intent:// URL reliably breaks the page out of the social app's
  // in-app webview into Chrome, where Google sign-in works. Auto-fire it once on
  // mount so the vast majority of users escape without having to read anything —
  // the big manual button below covers the rare case it doesn't take. Guarded so
  // it can't loop if the user comes back to the webview.
  useEffect(() => {
    if (!android) return;
    try {
      if (sessionStorage.getItem("tork_inapp_escape")) return;
      sessionStorage.setItem("tork_inapp_escape", "1");
    } catch {
      // sessionStorage unavailable — fall through and still attempt once.
    }
    setAutoOpening(true);
    const t = setTimeout(() => {
      try {
        window.location.href = androidChromeIntentUrl();
      } catch {
        /* no-op — manual button remains */
      }
    }, 500);
    return () => clearTimeout(t);
  }, [android]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      setCopied(false);
    }
  };

  const handleSendLink = async () => {
    if (!email.trim() || sending) return;
    setSending(true);
    try {
      await fetch("/api/auth/magic/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.trim() }),
      });
      setSent(true);
    } catch {
      // Generic response either way — surface the same confirmation.
      setSent(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="card-in-app-browser">
      <div className="rounded-xl border border-[#F6642A]/40 bg-[#F6642A]/10 p-5 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#F6642A]/15">
          <ExternalLink className="h-6 w-6 text-[#F6642A]" />
        </div>
        <p className="text-lg font-bold text-foreground">One quick step to continue</p>
        <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
          {android
            ? "We're opening this page in your browser so you can sign in with Google. If it doesn't open on its own, tap the button below."
            : "Facebook & Instagram block Google sign-in inside their built-in browser. Open this page in Safari to continue — it only takes a few seconds."}
        </p>
      </div>

      {android ? (
        <a href={androidChromeIntentUrl()} data-testid="link-open-chrome" className="block">
          <Button
            type="button"
            className="w-full h-14 text-base font-semibold gap-2 bg-[#F6642A] hover:bg-[#F6642A]/90 text-white"
            data-testid="button-open-chrome"
          >
            {autoOpening ? <Loader2 className="h-5 w-5 animate-spin" /> : <ExternalLink className="h-5 w-5" />}
            Open in Chrome to continue
          </Button>
        </a>
      ) : (
        <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-4">
          <ol className="space-y-3 text-sm text-foreground">
            <li className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#F6642A]/15 text-xs font-bold text-[#F6642A]">1</span>
              <span className="leading-relaxed pt-0.5">
                Tap the{" "}
                {ios ? <Share className="inline h-4 w-4 -mt-0.5" /> : <MoreHorizontal className="inline h-4 w-4 -mt-0.5" />}
                {" "}
                <span className="font-semibold">{ios ? "share" : "menu (•••)"}</span> button — usually at the top-right or bottom of this app.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#F6642A]/15 text-xs font-bold text-[#F6642A]">2</span>
              <span className="leading-relaxed pt-0.5">
                Choose <span className="font-semibold">{ios ? '"Open in Safari"' : '"Open in browser"'}</span>.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#F6642A]/15 text-xs font-bold text-[#F6642A]">3</span>
              <span className="leading-relaxed pt-0.5">Sign in with Google there as normal.</span>
            </li>
          </ol>
          <Button
            type="button"
            className="w-full h-12 gap-2 bg-[#F6642A] hover:bg-[#F6642A]/90 text-white"
            onClick={handleCopy}
            data-testid="button-copy-url"
          >
            {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
            {copied ? `Link copied — paste it in ${ios ? "Safari" : "your browser"}` : "Copy link"}
          </Button>
        </div>
      )}

      {showEmail ? (
        sent ? (
          <div className="rounded-lg border border-green-500/40 bg-green-500/10 p-4 text-sm" data-testid="text-magic-sent">
            <div className="flex items-start gap-2.5">
              <Mail className="h-5 w-5 text-green-500 mt-0.5 shrink-0" />
              <p className="text-foreground leading-relaxed">
                If an account exists for that email, a sign-in link is on its way. Open it on this device — it works in any browser and expires in 15 minutes.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-2 rounded-lg border border-border bg-muted/20 p-4">
            <p className="text-xs text-muted-foreground leading-relaxed">
              Already a member? Enter your account email and we'll send a one-time sign-in link that works right here.
            </p>
            <Input
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="input-magic-email"
            />
            <Button
              type="button"
              variant="outline"
              className="w-full h-11 gap-2"
              onClick={handleSendLink}
              disabled={!tosChecked || !email.trim() || sending}
              data-testid="button-send-magic-link"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              Email me a sign-in link
            </Button>
            {!tosChecked && (
              <p className="text-xs text-muted-foreground text-center">Please agree to the terms above first.</p>
            )}
          </div>
        )
      ) : (
        <button
          type="button"
          onClick={() => setShowEmail(true)}
          className="w-full text-center text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
          data-testid="button-show-email-signin"
        >
          Already have an account? Email me a sign-in link instead
        </button>
      )}
    </div>
  );
}

function GoogleSignInButton({ label, disabled }: { label: string; disabled?: boolean }) {
  const [loading, setLoading] = useState(false);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      // Forward any captured referral code so the server can stash it in the
      // session and bind referredByAccountId on the new-user branch of the
      // OAuth callback. localStorage.dd_ref is set by Router on mount.
      let qs = "";
      try {
        const params = new URLSearchParams();
        const ref = localStorage.getItem("dd_ref");
        if (ref) params.set("ref", ref);
        const src = localStorage.getItem("dd_lead_source");
        if (src) params.set("source", src);
        if (Array.from(params).length) qs = `?${params.toString()}`;
      } catch {}
      const res = await fetch(`/api/google/login-url${qs}`, { credentials: "include" });
      const data = await res.json();
      if (data.authUrl) {
        window.location.href = data.authUrl;
      }
    } catch {
      setLoading(false);
    }
  };

  return (
    <Button
      type="button"
      className="w-full h-12 text-base font-semibold gap-3 bg-[#F6642A] hover:bg-[#F6642A]/90 text-white"
      onClick={handleGoogleSignIn}
      disabled={loading || disabled}
      data-testid="button-google-signin"
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <SiGoogle className="h-4 w-4" />}
      {label}
    </Button>
  );
}

export default function AuthPage({ trial = false }: { trial?: boolean }) {
  const [mode, setMode] = useState<"login" | "register">(trial ? "register" : "login");
  const [tosChecked, setTosChecked] = useState(false);
  const [inApp] = useState(() => isInAppBrowser());
  const [magicInvalid] = useState(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("magic") === "invalid";
  });
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return null;
  }

  return (
    <div className="flex min-h-screen" data-testid="page-auth">
      <div className="flex flex-col justify-center w-full max-w-md mx-auto px-8 lg:mx-0 lg:w-1/2 lg:max-w-none lg:px-20">
        <div className="max-w-md mx-auto w-full">
          <div className="flex justify-center mb-8">
            <img src={logoBlack} alt="Tork" className="h-56 block dark:hidden" data-testid="logo-auth" />
            <img src={logoWhite} alt="Tork" className="h-56 hidden dark:block" />
          </div>

          <div className="mb-8">
            {trial && (
              <div className="inline-flex items-center gap-2 px-3 py-1 mb-3 rounded-full border border-[#F6642A]/30 bg-[#F6642A]/5 text-xs font-semibold text-[#F6642A]" data-testid="badge-trial">
                <DollarSign className="h-3.5 w-3.5" />
                $1 Trial — full access for 3 days
              </div>
            )}
            <h1 className="text-3xl font-bold text-foreground" data-testid="text-auth-headline">
              {trial ? "Start your $1 trial" : mode === "login" ? "Clicks. Calls. Customers. Cash." : "Get Started"}
            </h1>
            <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
              {trial
                ? "Create your account to claim your $1 trial. Full access to rank tracking, AI posts, review automation and more."
                : mode === "login"
                ? "Google is the #1 lead source for local businesses — when it’s done right. We’ve helped 5,000+ businesses get it right. You’re next."
                : "Create your account to get started."}
            </p>
          </div>

          <div className="space-y-4">
            <label
              className="flex items-start gap-2.5 p-3 rounded-lg border border-border bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
              data-testid="label-tos-agree"
            >
              <Checkbox
                checked={tosChecked}
                onCheckedChange={(v) => setTosChecked(!!v)}
                className="mt-0.5"
                data-testid="checkbox-tos-agree"
              />
              <span className="text-xs text-muted-foreground leading-relaxed">
                I agree to the{" "}
                <a href="/terms" target="_blank" rel="noreferrer" className="text-primary font-medium hover:underline" data-testid="link-tos-terms">Terms of Service</a>
                {" "}and{" "}
                <a href="/privacy" target="_blank" rel="noreferrer" className="text-primary font-medium hover:underline" data-testid="link-tos-privacy">Privacy Policy</a>.
              </span>
            </label>

            {magicInvalid && (
              <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-foreground" data-testid="text-magic-invalid">
                That sign-in link is invalid or has expired. Request a new one below.
              </div>
            )}

            {inApp ? (
              <InAppBrowserCard tosChecked={tosChecked} />
            ) : (
              <>
                <GoogleSignInButton
                  label={mode === "login" ? "Sign in with Google" : "Sign up with Google"}
                  disabled={!tosChecked}
                />

                <p className="text-xs text-muted-foreground text-center leading-relaxed">
                  We use Google sign-in so we can securely connect to your Google Business Profile.
                </p>
              </>
            )}

            <p className="text-sm text-muted-foreground text-center">
              {mode === "login" ? (
                <>
                  Don't have an account?{" "}
                  <button
                    type="button"
                    onClick={() => setMode("register")}
                    className="font-semibold text-primary hover:underline"
                    data-testid="link-signup"
                  >
                    Sign Up
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{" "}
                  <button
                    type="button"
                    onClick={() => setMode("login")}
                    className="font-semibold text-primary hover:underline"
                    data-testid="link-login"
                  >
                    Log In
                  </button>
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center p-12 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#f6642a]/20 via-[#0081df]/10 to-[#f6642a]/5 animate-gradient" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#f6642a]/15 via-transparent to-transparent" />
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#f6642a]/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#0081df]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
        <div className="relative max-w-lg text-center space-y-6 z-10">
          <div
            className="w-full rounded-2xl border border-[#1E1E2E] overflow-hidden"
            style={{ padding: "56.25% 0 0 0", position: "relative" }}
            data-testid="vsl-video-wrapper-auth"
          >
            <iframe
              src="https://player.vimeo.com/video/1198390423?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1&muted=1"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
              title="Local Business Owners: Get Into Google Maps' Top 3 For $1 - Tork Powered By Digital Domination"
              data-testid="vsl-video-auth"
            />
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#F6642A]/30 bg-[#F6642A]/5 text-sm text-[#F6642A] font-medium" data-testid="badge-social-proof">
            35+ years &middot; 5,000+ businesses &middot; AI-powered
          </div>
          <div className="grid grid-cols-2 gap-4 pt-4">
            <div className="p-4 rounded-2xl bg-background/80 backdrop-blur border border-border/50 text-left" data-testid="card-feature-geogrid">
              <MousePointer className="w-5 h-5 text-[#F6642A] mb-2" />
              <div className="text-sm font-bold text-foreground">See who's beating you</div>
              <div className="text-xs text-muted-foreground mt-0.5">Rank Tracking</div>
            </div>
            <div className="p-4 rounded-2xl bg-background/80 backdrop-blur border border-border/50 text-left" data-testid="card-feature-posts">
              <Phone className="w-5 h-5 text-[#F6642A] mb-2" />
              <div className="text-sm font-bold text-foreground">Stay ahead on Google</div>
              <div className="text-xs text-muted-foreground mt-0.5">AI Post Scheduler</div>
            </div>
            <div className="p-4 rounded-2xl bg-background/80 backdrop-blur border border-border/50 text-left" data-testid="card-feature-reviews">
              <Star className="w-5 h-5 text-[#F6642A] mb-2" />
              <div className="text-sm font-bold text-foreground">Win the trust battle</div>
              <div className="text-xs text-muted-foreground mt-0.5">Review Management</div>
            </div>
            <div className="p-4 rounded-2xl bg-background/80 backdrop-blur border border-border/50 text-left" data-testid="card-feature-listing">
              <DollarSign className="w-5 h-5 text-[#F6642A] mb-2" />
              <div className="text-sm font-bold text-foreground">Lock down your turf</div>
              <div className="text-xs text-muted-foreground mt-0.5">24/7 Protection</div>
            </div>
          </div>
          <p className="text-sm text-muted-foreground pt-2" data-testid="text-social-proof-bottom">
            Join 5,000+ businesses stealing market share from their competitors.
          </p>
        </div>
      </div>
    </div>
  );
}
